// Updated Dashboard.tsx with data-labels for mobile accessibility

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../components/Navbar";
import "../styles/Dashboard.css";

interface UserData {
  givenNames: string;
  familyName: string;
  email?: string;
  phoneNumber?: string;
}

export interface Report {
  NCJPRecordID: string;
  recordType: string;
  recordType_cy?: string;
  description: string;
  description_cy?: string;
  personRole: string;
  personRole_cy?: string;
  dateCreated: string;
  dateCreated_cy?: string;
  readMessages?: number;
  unreadMessages?: number;
  organisationLabel: string;
  organisationID: string;
  pncID: string;
}

interface ApiResponse {
  status: string;
  data: {
    user: UserData;
    reports: Report[];
  };
}

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation("translation");
  const translate = t as unknown as (key: string, options?: any) => string;

  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const isWelsh = i18n.language === "cy";

  const logError = (message: string) => {
    try {
      fetch("./error_log.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ error: message }),
      })
        .then(() => console.log("Error logged successfully:", message))
        .catch((err) => console.error("Failed to log error to the server:", err));
    } catch (e: any) {
      console.error("Error logging failed:", e.message);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const oneLoginSub = localStorage.getItem("userId");

    if (!token || !oneLoginSub) {
      console.error("No authentication token or userId found. Redirecting...");
      navigate("/after-login");
      return;
    }

    const fetchUserData = () => {
      fetch(
        `https://api.psanalytics.co.uk/ncjp/getUserReports?oneLoginSub=${encodeURIComponent(
          oneLoginSub
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
        .then((response) => response.json())
        .then((data: ApiResponse) => {
          if (data.status !== "success" || !data.data || typeof data.data !== "object") {
            logError(`Unexpected API response format: ${JSON.stringify(data)}`);
            setErrorMessage(
              translate("dashboardInvitationMessage") ||
                "National My Police Portal is currently on an invitation-only basis."
            );
            return;
          }
          setUserData(data.data.user);
          setReports(data.data.reports);
        })
        .catch((error) => {
          logError(`Failed to fetch user data: ${error}`);
          setErrorMessage(
            translate("dashboardInvitationMessage") ||
              "National My Police Portal is currently on an invitation-only basis."
          );
        });
    };

    fetchUserData();
  }, [navigate, translate]);

  const groupedReports = reports.reduce(
    (groups: Record<string, Report[]>, report) => {
      const { pncID } = report;
      if (!groups[pncID]) groups[pncID] = [];
      groups[pncID].push(report);
      return groups;
    },
    {}
  );

  return (
    <div>
      <Navbar />
      <main className="container">
        <h2 className="text-center mt-4">
          {translate("recentReports") || "Recent Reports and Service Requests"}
        </h2>

        {errorMessage && (
          <div className="alert alert-warning text-center my-4">
            {errorMessage}
          </div>
        )}

        {userData && (
          <div id="user-info" className="my-4">
            <h3>
              {userData.givenNames} {userData.familyName}
            </h3>
            <p>{translate("email")}: {userData.email || translate("notProvided")}</p>
            <p>{translate("telephone")}: {userData.phoneNumber || translate("notProvided")}</p>
          </div>
        )}

        <div id="organisations-container">
          {Object.keys(groupedReports).map((pncID) => {
            const orgReports = groupedReports[pncID];
            const organisationLabel =
              orgReports[0]?.organisationLabel || translate("unknownOrganisation");
            const organisationCrestUrl = `/assets/crests/${pncID}.jpg`;

            return (
              <div key={pncID} className="organisation-block">
                <div className="organisation-header">
                  <img
                    className="invite-logo"
                    src={organisationCrestUrl}
                    alt={translate("organisationCrestAlt", { organisation: organisationLabel })}
                  />
                  <h4>{translate("organisationLabel", { organisation: organisationLabel })}</h4>
                </div>
                <div className="table-wrapper">
                  <table className="organisation-table">
                    <thead>
                      <tr>
                        <th>{translate("reportID")}</th>
                        <th>{translate("recordType")}</th>
                        <th>{translate("description")}</th>
                        <th>{translate("role")}</th>
                        <th>{translate("dateCreated")}</th>
                        <th>{translate("messages")}</th>
                        <th>{translate("actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgReports.map((report) => (
                        <tr key={report.NCJPRecordID} className="responsive-row">
                          <td data-label={translate("reportID")}>{report.NCJPRecordID}</td>
                          <td data-label={translate("recordType")}>
                            {isWelsh && report.recordType_cy ? report.recordType_cy : report.recordType}
                          </td>
                          <td data-label={translate("description")}>
                            {isWelsh && report.description_cy ? report.description_cy : report.description}
                          </td>
                          <td data-label={translate("role")}>
                            {isWelsh && report.personRole_cy ? report.personRole_cy : report.personRole}
                          </td>
                          <td data-label={translate("dateCreated")}>
                            {isWelsh && report.dateCreated_cy ? report.dateCreated_cy : report.dateCreated}
                          </td>
                          <td data-label={translate("messages")}>
                            {translate("read")}: {report.readMessages || 0} <br />
                            <span className={report.unreadMessages ? "unread-highlight" : ""}>
                              {translate("unread")}: {report.unreadMessages || 0}
                            </span>
                          </td>
                          <td data-label={translate("actions")}>
                            <button
                              type="button"
                              className="govuk-button"
                              onClick={() => {
                                const oneLoginSub = localStorage.getItem("userId") || "";
                                navigate(`/organisation/${report.organisationID}/${report.NCJPRecordID}?oneLoginSub=${encodeURIComponent(oneLoginSub)}&pncID=${encodeURIComponent(report.pncID)}`);
                              }}
                            >
                              {translate("viewRecord")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;